<template>
  <div class="track-wrapper">
    <div class="track" v-if="track.id">
      <div
        :class="{
          'track-line row mt-2 ml-0 pr-1': true,
          'mb-0': showDetails,
          'mb-2': !showDetails,
          failed: failed,
          fulfilled: fulfilled,
        }"
      >
        <template v-if="track !== undefined">
          <div class="col s2 m2 l1 small">
            <div
              class="switch-container"
              :class="{
                'mt-1': true,
                'green-text': !locked,
                'red-text': locked,
              }"
            >
              <div class="switch">
                <label>
                  <input v-model="active" type="checkbox" />
                  <span class="lever"></span>
                </label>
              </div>
              <span class="hint by-switch">
                {{ lockedText }}
              </span>
            </div>
          </div>
          <div class="col s7 m3 l2 small" @click="setEditStep(3)">
            <div class="track-part action fill-height">
              <span :class="`pr-2 fa ${actionIcon}`" :tile="actionText" />
              <span v-if="track.name">
                {{ track.name }}
              </span>
              <span v-else>
                {{ capitalize(actionText) }}
              </span>
            </div>
          </div>
          <div
            @click="setEditStep(4)"
            class="col m2 l2 hide-on-small-only params small px-0 pb-0 pt-1"
          >
            <div
              class="track-part param mb-1 center"
              :title="stringify(getParamValue(param.name))"
              :key="param.name"
              v-for="param in selectedActionParamsWithValuesMain"
            >
              {{ getParamValue(param.name) | truncate(100) }}
            </div>
            <div
              class="fill-height w-100 pb-1"
              v-if="!track.params || Object.keys(track.params).length === 0"
            >
              <div class="track-part param fill-height fill-width"> </div>
            </div>
          </div>
          <div
            class="col m2 l2 hide-on-small-only small"
            @click="setEditStep(1)"
          >
            <div class="track-part event fill-height">
              {{ offsetText }}
              {{ eventText }}
            </div>
          </div>
          <div
            class="col m2 l2 hide-on-small-only small conditions px-0 pb-0 pt-1"
            @click="setEditStep(2)"
          >
            <div
              class="mb-1 track-part condition"
              v-for="condition in track.conditions"
              :key="condition.uuid"
            >
              {{ conditionText(condition) }}
            </div>
            <div
              class="mb-1 track-part condition"
              v-if="!track.conditions || track.conditions.length === 0"
            >
              on all inspections
            </div>
          </div>
          <div
            @click="setEditStep(4)"
            class="col l2 hide-on-med-and-down params small pl-1 pr-0 pb-0 pt-1"
          >
            <div
              class="track-part param mb-1"
              :title="stringify(getParamValue(param.name))"
              :key="param.name"
              v-for="param in selectedActionParamsWithValuesSecondary"
            >
              <strong> {{ param.name }}: </strong>
              &nbsp;
              {{ getParamValue(param.name) | truncate(40) }}
            </div>
            <div
              class="fill-height w-100 pb-1"
              v-if="!track.params || Object.keys(track.params).length === 0"
            >
              <div class="track-part param fill-height fill-width"> </div>
            </div>
          </div>
          <div class="col s1 small">
            <div @click="duplicate">
              <i class="fa fa-copy light" />
            </div>
            <div class="vpx-1" @click="edit = !edit">
              <i class="fa fa-pencil" :class="{ light: !edit }" />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div v-if="edit" class="edit-expansion">
      <div v-if="!restricted" class="edit-header">
        <div
          @click="setEditStep(1)"
          class="edit-btn"
          :class="{ active: editStep === 1, past: editStep > 1 }"
        >
          <div class="edit-circle">
            <span v-if="editStep === 1">1</span>
            <span v-else class="fa fa-pencil" />
          </div>
          <div class="edit-text hide-on-small-only">Choose an Event</div>
        </div>
        <div class="divider" />
        <div
          @click="setEditStep(2)"
          class="edit-btn"
          :class="{ active: editStep === 2, past: editStep > 2 }"
        >
          <div class="edit-circle">
            <span v-if="editStep < 3">2</span>
            <span v-else class="fa fa-pencil" />
          </div>
          <div class="edit-text hide-on-small-only">Add Rules</div>
        </div>
        <div class="divider" />
        <div
          @click="setEditStep(3)"
          class="edit-btn"
          :class="{ active: editStep === 3, past: editStep > 3 }"
        >
          <div class="edit-circle">
            <span v-if="editStep < 4">3</span>
            <span v-else class="fa fa-pencil" />
          </div>
          <div class="edit-text hide-on-small-only">Choose an Action</div>
        </div>
        <div class="divider" />
        <div
          @click="setEditStep(4)"
          class="edit-btn"
          :class="{ active: editStep === 4 }"
        >
          <div class="edit-circle">4</div>
          <div class="edit-text hide-on-small">Add Details</div>
        </div>
      </div>
      <div class="edit-body">
        <template v-if="editStep === 1">
          <div class="row fields">
            <div class="action-color blue">&nbsp;</div>
            <div class="col m2 s6">
              <input class="event-offset" type="number" v-model="eventOffset" />
            </div>
            <div class="col m2 s6">
              <select v-model="track.offset_multiplier">
                <option
                  v-for="(option, idx) in unitOptions"
                  :key="idx"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
            <div class="col m4 s6">
              <select v-model="eventOffsetModifier">
                <option
                  v-for="(option, idx) in eventOffsetOptions"
                  :key="idx"
                  :value="option.value"
                  :disabled="option.disabled"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="row fields">
            <div class="action-color blue">&nbsp;</div>
            <div class="col s12">
              <select v-model="selectedEvent">
                <option
                  v-for="(option, idx) in eventOptions"
                  :key="idx"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
              <label v-if="hint">{{ hint }}</label>
            </div>
          </div>
          <div class="row fields">
            <div class="action-color blue">&nbsp;</div>
            <div class="col m4 s12 scheduling-options">
              <p>
                <input
                  type="checkbox"
                  :name="track.id + '-certain'"
                  :checked="useWindow"
                />
                <label @click="toggleUseWindow" :for="track.id + '-certain'"
                  >Send during certain hours only</label
                >
              </p>
              <p>
                <input
                  type="checkbox"
                  :name="track.id + '-weekends'"
                  :checked="avoidWeekend"
                />
                <label
                  @click="avoidWeekend = !avoidWeekend"
                  :for="track.id + '-weekends'"
                  >Do not send on weekends</label
                >
              </p>
            </div>
            <div class="col m8 s12 range-container">
              <template v-if="useWindow">
                <div class="center small mb-1 mt-3">
                  <span class="medium-gray">From</span>
                  {{ eventTimeWindowStart }}
                  <span class="medium-gray">To</span>
                  {{ eventTimeWindowEnd }}
                </div>
                <range-slider
                  v-model="eventTimeWindow"
                  :range="{ min: 0, max: windowTimes.length - 1 }"
                />
              </template>
            </div>
          </div>
        </template>
        <template v-if="editStep === 2">
          <div
            v-for="(condition, cIdx) in track.conditions"
            :key="condition.id"
          >
            <div class="row fields">
              <div class="action-color dark-blue">&nbsp;</div>
              <div class="col m3 s6">
                <select v-model="condition.inclusive">
                  <option
                    v-for="(option, idx) in conditionInclusiveOptions"
                    :key="idx"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
              </div>
              <div class="col m4 s6">
                <select v-model="condition.field">
                  <option
                    v-for="(option, idx) in conditionFieldOptions"
                    :key="idx"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
              </div>
              <div class="col m4 s6">
                <select
                  v-model="condition.value"
                  v-if="!conditionIsBoolean(condition)"
                >
                  <option
                    v-for="(option, idx) in conditionValueOptions(condition)"
                    :key="idx"
                    :value="option.value"
                  >
                    {{ option.text }}
                  </option>
                </select>
              </div>
              <div class="col m1 s6 pt-4 delete-column">
                <div @click="removeCondition(cIdx)" class="fa fa-trash" />
              </div>
            </div>
          </div>

          <div v-if="track.conditions.length === 0" class="bordered-row mb-2">
            <div class="row fields">
              <div class="action-color dark-blue">&nbsp;</div>

              <div class="center padded">
                Without rules, this is applied to all inspections.
              </div>
            </div>
          </div>

          <div class="row center">
            <div @click="addRule" class="btn grey-btn">
              <div class="fa fa-plus" />
              Add Rule
            </div>
          </div>
        </template>
        <template v-if="editStep === 3">
          <div class="bordered-row">
            <div class="row fields">
              <div class="action-color orange">&nbsp;</div>
              <select
                v-model="track.auto_action"
                @change="clearParams"
                class="action-select"
              >
                <option
                  v-for="(action, idx) in actionOptions"
                  :key="idx"
                  :value="action.value"
                >
                  {{ action.text }}
                </option>
              </select>
            </div>
          </div>
        </template>
        <template v-if="editStep === 4">
          <div class="center">
            Provide details necessary to
            <strong>{{ humanize(track.auto_action) }}</strong>
          </div>
          <div class="row mt-0" v-if="selectedParams">
            <div
              :class="paramColumnClass(param)"
              v-for="param in selectedActionParams"
              :key="param.name"
            >
              <div class="row fields param">
                <div class="action-color action-green">&nbsp;</div>
                <div
                  v-if="param.control === 'combobox'"
                  class="input-container"
                >
                  <combo-box
                    :typable="true"
                    :multiple="true"
                    :label="
                      humanize(param.name) +
                      ' (type a value or select one from the list)'
                    "
                    :items="param.special_values"
                    v-model="selectedParams[param.name]"
                  />
                </div>
                <div v-if="param.control === 'select'" class="input-container">
                  <combo-box
                    :typable="false"
                    :multiple="param.multiple"
                    :label="humanize(param.name)"
                    :items="param.special_values"
                    v-model="selectedParams[param.name]"
                  />
                </div>
                <div
                  v-if="param.control === 'text-field'"
                  class="input-container"
                >
                  <label :for="param.name + track.id">{{
                    humanize(param.name)
                  }}</label>
                  <input
                    v-model="selectedParams[param.name]"
                    :name="param.name + track.id"
                  />
                </div>
                <div
                  v-if="param.control === 'textarea'"
                  class="input-container"
                >
                  <label :for="param.name + track.id">{{
                    humanize(param.name)
                  }}</label>
                  <textarea
                    v-model="selectedParams[param.name]"
                    :name="param.name + track.id"
                  />
                </div>
                <div
                  class="pb-3"
                  v-if="param.control === 'htmleditor-with-placeholders'"
                >
                  <placeholder-editor
                    @change="emitTrack"
                    :label="humanize(param.name)"
                    :is-html="true"
                    v-model="selectedParams[param.name]"
                  />
                </div>
                <div
                  class="pb-3"
                  v-if="param.control === 'textarea-with-placeholders'"
                >
                  <placeholder-editor
                    @change="emitTrack"
                    :label="humanize(param.name)"
                    :is-html="false"
                    v-model="selectedParams[param.name]"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
        <div class="row center">
          <div
            class="btn btn-primary"
            v-if="editStep !== 4"
            @click="setEditStep(editStep + 1)"
          >
            to {{ nextStepName }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Inflect from 'i'
import uuid from 'uuid4'
import dig from '../../../Dig'
import ComboBox from './ComboBox'
import PlaceholderEditor from './PlaceholderEditor'
import RangeSlider from '../../RangeSlider'

const inflect = Inflect()

const stringify = (value) => {
  if (Array.isArray(value)) {
    value = `[${value.join(', ')}]`
  }
  return value.replace(/(<([^>]+)>)/gi, '')
}
const mainParams = ['to', 'url']
const secParams = {
  send_email: ['subject'],
  send_text: ['body'],
  call_webhook: ['payload'],
}

const windowTimes = [
  '12:30 am',
  '1:00 am',
  '1:30 am',
  '2:00 am',
  '2:30 am',
  '3:00 am',
  '3:30 am',
  '4:00 am',
  '4:30 am',
  '5:00 am',
  '5:30 am',
  '6:00 am',
  '6:30 am',
  '7:00 am',
  '7:30 am',
  '8:00 am',
  '8:30 am',
  '9:00 am',
  '9:30 am',
  '10:00 am',
  '10:30 am',
  '11:00 am',
  '11:30 am',
  '12:00 pm',
  '12:30 pm',
  '1:00 pm',
  '1:30 pm',
  '2:00 pm',
  '2:30 pm',
  '3:00 pm',
  '3:30 pm',
  '4:00 pm',
  '4:30 pm',
  '5:00 pm',
  '5:30 pm',
  '6:00 pm',
  '6:30 pm',
  '7:00 pm',
  '7:30 pm',
  '8:00 pm',
  '8:30 pm',
  '9:00 pm',
  '9:30 pm',
  '10:00 pm',
  '10:30 pm',
  '11:00 pm',
  '11:30 pm',
]

const defaultWindowStart = '7:00 am'
const defaultWindowEnd = '5:00 pm'
const defaultWindowStartIndex = windowTimes.findIndex(
  (x) => x === defaultWindowStart
)
const defaultWindowEndIndex = windowTimes.findIndex(
  (x) => x === defaultWindowEnd
)

export default {
  name: 'auto-track',
  components: {
    ComboBox,
    PlaceholderEditor,
    RangeSlider,
  },
  computed: {
    actionOptions() {
      return this.actions.map((a) => {
        return {
          text: inflect.humanize(a.name),
          value: a.name,
        }
      })
    },
    actionIcon() {
      switch (this.track.auto_action) {
        case 'send_email':
          return 'fa-envelope-o'
        case 'send_text':
          return 'fa-commenting-o'
        case 'call_webhook':
          return 'fa-terminal'
      }
      return ''
    },
    actionText() {
      return inflect.humanize(this.track.auto_action).toLowerCase()
    },
    canPretriggerEvent() {
      let event = this.events.find((x) => x.name === this.selectedEvent)
      if (!event) {
        return false
      }
      return event.can_pretrigger
    },
    conditionFieldOptions() {
      return this.conditions.map((c) => {
        return {
          text: inflect.humanize(c.label),
          value: c.name,
        }
      })
    },
    conditionInclusiveOptions() {
      return [
        {
          text: 'Having',
          value: true,
        },
        {
          text: 'Not having',
          value: false,
        },
      ]
    },
    details() {
      if (this.track && this.track && this.track.details) {
        return this.track.details
      }
      return []
    },
    event() {
      let eventName = this.selectedEvent
      if (eventName === undefined || eventName === '') {
        return {}
      }
      return this.events.find((x) => x.name === eventName)
    },
    eventOffsetOptions() {
      if (this.selectedEvent === undefined) {
        return []
      }
      let beforeOption = {
        text: 'Before',
        value: 'before',
      }
      if (!this.canPretriggerEvent) {
        beforeOption.disabled = true
      }
      let options = [
        beforeOption,
        {
          text: 'After',
          value: 'after',
        },
      ]
      return options
    },
    eventOptions() {
      return this.events.map((e) => {
        return {
          text: e.label,
          value: e.name,
          disabled: e.disabled,
        }
      })
    },
    eventSpec() {
      return this.events.find((e) => e.name === this.selectedEvent)
    },
    eventText() {
      return this.event.label.toLowerCase()
    },
    eventTimeWindowStart() {
      return windowTimes[this.eventTimeWindow[0]]
    },
    eventTimeWindowEnd() {
      return windowTimes[this.eventTimeWindow[1]]
    },
    failed() {
      if (this.track && this.track) {
        return this.track.is_failed
      }
      return false
    },
    fulfilled() {
      if (this.track && this.track) {
        return this.track.is_fulfilled
      }
      return false
    },
    hint() {
      if (this.eventSpec) {
        return this.eventSpec.hint
      }
      return null
    },
    locked() {
      return !this.active
    },
    lockedText() {
      return this.active ? 'Enabled' : 'Disabled'
    },
    nextStepName() {
      switch (this.editStep) {
        case 1:
          return "'Add Rules'"
        case 2:
          return "'Choose an Action'"
        case 3:
          return "'Add Details'"
        default:
          return ''
      }
    },
    offsetText() {
      let offsetMultiplier = this.track.offset_multiplier || 3600
      let offsetMod = 1
      if (this.eventOffsetModifier === '') offsetMod = 0
      if (this.eventOffsetModifier === 'before') offsetMod = -1
      let offset = this.eventOffset * this.track.offset_multiplier * offsetMod

      if (offset === undefined) {
        return ''
      }
      if (offset === 0) {
        return 'At time of'
      }
      let humanWord = 'hours'

      switch (offsetMultiplier) {
        case 24 * 3600 * 30:
          humanWord = 'months'
          break
        case 24 * 3600 * 7:
          humanWord = 'weeks'
          break
        case 24 * 3600:
          humanWord = 'days'
          break
        case 60:
          humanWord = 'minutes'
          break
      }
      let humanOffset = offset / offsetMultiplier
      let directionWord = 'after'
      if (humanOffset === 1 || humanOffset === -1) {
        humanWord = inflect.singularize(humanWord)
      }
      if (offset < 0) {
        directionWord = 'before'
        humanOffset *= -1
      }
      return [humanOffset, humanWord, directionWord].join(' ')
    },
    selectedActionParams() {
      const action = this.actions.find((a) => a.name === this.track.auto_action)
      if (action === undefined) {
        return []
      }
      return action.params
    },
    selectedActionParamsWithValues() {
      return this.selectedActionParams.filter((param) =>
        this.filterParams(param)
      )
    },
    selectedActionParamsWithValuesMain() {
      return this.selectedActionParams.filter((param) =>
        this.filterParams(param, 'main')
      )
    },
    selectedActionParamsWithValuesSecondary() {
      return this.selectedActionParams.filter((param) =>
        this.filterParams(param, 'secondary')
      )
    },
    unitOptions() {
      return [
        {
          text: 'minutes',
          value: 60,
        },
        {
          text: 'hours',
          value: 3600,
        },
        {
          text: 'days',
          value: 3600 * 24,
        },
        {
          text: 'weeks',
          value: 3600 * 24 * 7,
        },
        {
          text: 'months',
          value: 3600 * 24 * 30,
        },
      ]
    },
  },
  data() {
    return {
      active: true,
      avoidWeekend: false,
      deleting: false,
      edit: false,
      editStep: 1,
      eventTimeWindow: [defaultWindowStartIndex, defaultWindowEndIndex],
      eventOffset: 0,
      eventOffsetModifier: '',
      selectedEvent: '',
      selectedParams: null,
      showDetails: true,
      track: {},
      useWindow: false,
      windowTimes,
    }
  },
  filters: {
    truncate(value, limit) {
      value = stringify(value)
      if (value && value.length > limit) {
        value = value.substring(0, limit - 3) + '...'
      }
      return value
    },
  },
  methods: {
    addRule() {
      this.track.conditions.push({
        id: uuid(),
        inclusive: true,
        operator: 'equal_to',
      })
    },
    clearParams() {
      this.selectedParams = null
    },
    collectedTrack() {
      let cTrack = JSON.parse(JSON.stringify(this.track))

      cTrack.is_locked = !this.active

      let offsetMod = 1
      if (this.eventOffsetModifier === '') offsetMod = 0
      if (this.eventOffsetModifier === 'before') offsetMod = -1
      cTrack.offset = this.eventOffset * cTrack.offset_multiplier * offsetMod

      cTrack.scheduling_options.use_window = this.useWindow
      cTrack.scheduling_options.avoid_weekend = this.avoidWeekend
      if (this.useWindow) {
        cTrack.scheduling_options.window_start = this.eventTimeWindowStart
        cTrack.scheduling_options.window_end = this.eventTimeWindowEnd
      } else {
        delete cTrack.scheduling_options.window_start
        delete cTrack.scheduling_options.window_end
      }

      cTrack.params = this.selectedParams

      return cTrack
    },
    conditionIsBoolean(condition) {
      if (!condition.field) {
        return false
      }
      const conditionDef = this.conditions.find(
        (x) => x.name === condition.field
      )
      if (
        conditionDef.choices.length === 2 &&
        conditionDef.choices[0][1] === true &&
        conditionDef.choices[1][1] === false
      ) {
        return true
      }
      return false
    },
    capitalize(s) {
      if (!s) {
        return s
      }
      const head = s.slice(0, 1)
      const tail = s.slice(1)
      return head.toUpperCase() + tail
    },
    conditionText(condition) {
      if (!this.conditionValid(condition)) {
        return ''
      }
      let parts = []
      let conditionDef = this.conditions.find((x) => x.name === condition.field)
      if (condition.inclusive) {
        parts.push('having')
      } else {
        parts.push('not having')
      }
      parts.push(`${inflect.humanize(conditionDef.label).toLowerCase()}`)
      if (condition.operator !== 'equal_to') {
        parts.push(inflect.humanize(condition.operator).toLowerCase())
      }
      if (conditionDef) {
        if (condition.value !== true) {
          // Non-boolean condition
          const conditionChoice = conditionDef.choices.find(
            (x) => x[1] === condition.value
          )
          if (conditionChoice) {
            parts.push(`"${conditionChoice[0]}"`)
          } else {
            parts.push(`"${condition.value}"`)
          }
        }
      } else {
        parts.push(`"${condition.value}"`)
      }
      return parts.join(' ')
    },
    conditionValid(condition) {
      if (!condition.value || !condition.field) {
        return false
      }
      return true
    },
    conditionValueOptions(condition) {
      if (!condition.field) {
        return []
      }
      const conditionDef = this.conditions.find(
        (x) => x.name === condition.field
      )
      return conditionDef.choices.map(function (pair) {
        return {
          text: pair[0],
          value: pair[1],
        }
      })
    },
    duplicate() {
      let dup = JSON.parse(JSON.stringify(this.track))
      delete dup.id
      this.$emit('addDuplicate', dup)
    },
    emitTrack() {
      this.$emit('updatedRecord', this.collectedTrack())
    },
    filterParams(param, mode = null) {
      if (mode === 'main') {
        if (!mainParams.includes(param.name)) {
          return false
        }
      }
      if (mode === 'secondary') {
        if (!dig(secParams, this.track.auto_action, []).includes(param.name)) {
          return false
        }
      }
      let value = this.getParamValue(param.name)
      if (value === undefined) {
        return false
      }
      if (value === null) {
        return false
      }
      if (value.length === 0) {
        return false
      }
      return true
    },
    getParamValue(key) {
      if (this.track && this.selectedParams) {
        return this.selectedParams[key]
      }
      return undefined
    },
    humanize(val) {
      return inflect.humanize(val)
    },
    initializeParams() {
      if (Object.keys(this.selectedParams || {}).length > 0) {
        return
      }

      this.selectedParams = this.selectedActionParams.reduce((summ, param) => {
        summ[param.name] = param.default_value
        return summ
      }, {})
    },
    paramColumnClass(param) {
      let classParts = ['bordered-row', 'col', 'param']
      if (param.column_class) {
        classParts.push(param.column_class)
      } else {
        classParts.push('s12')
      }
      return classParts.join(' ')
    },
    prepareTrack() {
      this.active = !this.track.is_locked

      this.selectedEvent = this.track.auto_event

      let offsetMultiplier = this.track.offset_multiplier || 3600
      this.eventOffset = Math.abs(this.track.offset) / offsetMultiplier
      if (this.track.offset >= 0) {
        this.eventOffsetModifier = 'after'
      } else {
        this.eventOffsetModifier = 'before'
      }

      this.useWindow = !!this.track.scheduling_options.use_window
      this.avoidWeekend = !!this.track.scheduling_options.avoid_weekend

      if (
        this.track.scheduling_options.window_start &&
        this.track.scheduling_options.window_end
      ) {
        let windowStart = this.track.scheduling_options.window_start
        let windowEnd = this.track.scheduling_options.window_end
        let windowStartIndex = windowTimes.findIndex((x) => x === windowStart)
        let windowEndIndex = windowTimes.findIndex((x) => x === windowEnd)
        if (
          windowStartIndex > -1 &&
          windowEndIndex > -1 &&
          windowStartIndex <= windowEndIndex
        ) {
          this.eventTimeWindow = [windowStartIndex, windowEndIndex]
        }
      }

      this.selectedParams = JSON.parse(JSON.stringify(this.track.params))
    },
    removeCondition(idx) {
      this.track.conditions = this.track.conditions.filter((c, i) => {
        return i !== idx
      })
    },
    setEditStep(step) {
      if (this.restricted && step !== 4) return

      this.edit = true
      this.initializeParams()
      this.editStep = step
    },
    stringify,
    toggleUseWindow() {
      this.useWindow = !this.useWindow
    },
  },
  mounted() {
    this.track = JSON.parse(JSON.stringify(this.trackRun))
    this.prepareTrack()
    this.edit = !!this.editDefault
    if (this.restricted) this.editStep = 4
  },
  props: [
    'actions',
    'conditions',
    'companyId',
    'editDefault',
    'events',
    'restricted',
    'trackRun',
  ],
  watch: {
    active() {
      this.emitTrack()
    },
    avoidWeekend() {
      this.emitTrack()
    },
    eventOffset() {
      this.emitTrack()
    },
    eventOffsetModifier() {
      this.emitTrack()
    },
    eventTimeWindow() {
      this.emitTrack()
    },
    track: {
      handler: function () {
        this.emitTrack()
      },
      deep: true,
    },
    selectedEvent() {
      this.emitTrack()
    },
    selectedParams: {
      handler: function () {
        this.emitTrack()
      },
      deep: true,
    },
    useWindow() {
      this.emitTrack()
    },
  },
}
</script>

<style scoped lang="scss">
.details {
  margin: 0;
  padding: 3px 5px 0;
  background-color: white;
  border: 1px solid #ddd;
  border-top-width: 0;
  color: black;
  &.has-subruns {
    background-color: #f8f8f8;
  }
}

.track {
  display: flex;
  flex-direction: row;

  .drag-handle {
    display: flex;
    align-items: center;
    width: 14px;
    border: 1px solid #ccc;
    border-right: 0;
    background-color: #f8f8f8;
    flex-direction: row;
    .handle {
      display: flex;
      align-items: stretch;
      margin: 4px;
      width: 10px;
      border-left: 1px solid #888;
      border-right: 1px solid #888;
    }
  }

  .left-buttons {
    display: flex;
    align-items: flex-start;
    width: 56px;
    border: 1px solid #ccc;
    border-right: 0;
    flex-direction: column;
    padding: 12px;
    > div {
      cursor: pointer;
      margin-bottom: 6px;
      display: flex;
      flex-direction: row;
      align-items: center;
      line-height: 24px;
      i {
        line-height: 24px;
        margin-left: 10px;
        width: 14px;
      }
    }
    .hint {
      display: none;
    }
    &.show-help {
      width: 112px;
      .hint {
        display: inline-block;
        padding-left: 8px;
        &.by-switch {
          padding-left: 0;
        }
      }
    }
    .help-button {
      margin-top: auto;
    }
  }

  .track-line {
    text-align: left;
    margin: 0;
    padding-left: 0;
    padding-right: 10px;
    background-color: white;
    border: 1px solid #ccc;
    color: black;
    display: flex;
    align-items: stretch;
    flex-grow: 2;
    flex-direction: row;
    &.locked {
      background-color: #ddd;
    }
    &.failed {
      background-color: #fdd;
    }
    &.fulfilled {
      background-color: #dfd;
    }
    .col {
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      &.conditions,
      &.params {
        display: flex;
        flex-direction: column;
        .track-part {
          display: flex;
          &:last-child {
            flex-grow: 1;
            margin-bottom: 0;
          }
        }
      }
    }
    .track-part {
      width: 100%;
      text-align: center;
      align-items: center;
      border-radius: 3px;
      border: 1px solid #bbb;
      padding: 10px 5px;
      background-color: #f8f8f8;
      cursor: pointer;
      &.fill-height {
        display: flex;
        flex-direction: row;
        height: 100%;
        align-items: center;
        justify-content: center;
      }
      &.event {
        border-left: 4px solid #5c9ccf;
      }
      &.condition {
        border-left: 4px solid #45769c;
        text-align: left;
      }
      &.action {
        border-left: 4px solid #f57c00;
        .fa {
          padding-right: 0.25em;
        }
      }
      &.param {
        border-left: 4px solid #388e3c;
      }
    }
  }

  .w-100 {
    width: 100%;
  }
}

.switch:hover:after {
  display: block;
}

.switch:after {
  content: 'Turn this Action off in order to stop it from happening when you publish';
  position: absolute;
  color: #fff;
  background-color: #65c635;
  text-align: center;
  max-width: 25em;
  font-size: 0.75em;
  border-radius: 2px;
  margin-top: -7em;
  left: 0;
  display: none;
}

.edit-expansion {
  border: 1px solid #ccc;
  border-top: 2px solid #ccc;
  background-color: #fff;
}

.edit-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  height: 72px;
  background-color: #eee;
  box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);

  .edit-btn {
    cursor: pointer;
    padding: 24px;
    div {
      display: inline-block;
    }

    .edit-circle {
      text-align: center;
      background-color: rgba(0, 0, 0, 0.38);
      color: #fff;
      height: 24px;
      width: 24px;
      border-radius: 50%;
      margin-right: 5px;
    }

    .edit-text {
      color: rgba(0, 0, 0, 0.38);
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
      .edit-text {
        text-shadow: 0 0 0 #000;
      }
    }
  }

  .edit-btn.active,
  .edit-btn.past {
    .edit-circle {
      background-color: #5c9ccf;
    }

    .edit-text {
      text-shadow: 0 0 0 #000;
    }
  }

  .divider {
    border: none;
    display: block;
    height: 1px;
    -webkit-box-flex: 1;
    -ms-flex: 1 1;
    flex: 1 1;
    min-height: 1px;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.12);
    align-self: center;
    margin: 0 -16px;
  }
}

.edit-body {
  padding: 24px 24px 16px;

  .row.fields {
    border: 1px solid #bbb;
    border-radius: 3px;
    background-color: #fbfbfb;
    display: flex;
  }

  .action-color {
    display: inline-block;
    height: inherit;
    width: 5px;
    &.orange {
      background-color: #f9890e;
    }
    &.dark-blue {
      background-color: #45769c;
    }
    &.action-green {
      background-color: #388e3c;
    }
  }

  select {
    display: inline-block;
    background-color: inherit;
    border: none;
    cursor: pointer;
  }

  .padded {
    width: 100%;
    padding: 1em;
  }

  .grey-btn {
    background-color: #f5f5f5;
    color: rgba(0, 0, 0, 0.38);
    .fa {
      color: inherit;
    }
  }

  .delete-column {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .fa {
      cursor: pointer;
      color: rgba(0, 0, 0, 0.54);
    }
  }

  .scheduling-options {
    padding: 1em;
    p:last-of-type {
      margin-bottom: 0;
    }
  }

  .range-container {
    width: 100%;
    padding-top: 0.5em;
  }

  .event-offset {
    height: 2.5em;
  }

  .params {
    width: 100%;
  }

  .input-container {
    width: 100%;
    padding: 0.5em;
  }
  .pb-3 {
    width: 100%;
  }
}
</style>
